import parse from "html-react-parser";
import reactStringReplace from "react-string-replace";
import Image from "next/image";
import React from "react";

export function replaceKeywords(
  description: string,
  topDisplacement: boolean = true,
  isTrigger: boolean = false,
): React.ReactNode[] {
  // Escape all tags except <em> and <br>
  const escaped = description.replace(
    /<(?!\/?em\s*>|br\s*\/?>)[^>]+>/gi,
    (match) => match.replace(/</g, "&lt;").replace(/>/g, "&gt;"),
  );

  let replacedText = reactStringReplace(escaped);

  // Helper function for trigger replacements
  function applyTriggerReplacements(text: React.ReactNode[]) {
    if (isTrigger) {
      text = reactStringReplace(text, "[Raid]", (match, i) => (
        <Image
          key={`raid-trigger-${i}`}
          src="/union-arena/keywords/raid_trigger.png"
          alt="Raid Trigger"
          width={59}
          height={19}
          className="inline-block relative top-[-2px]"
        />
      ));
      text = reactStringReplace(text, "[Get]", (match, i) => (
        <Image
          key={`get-trigger-${i}`}
          src="/union-arena/keywords/get_trigger.png"
          alt="Get Trigger"
          width={59}
          height={19}
          className="inline-block relative top-[-2px]"
        />
      ));
      text = reactStringReplace(text, "[Active]", (match, i) => (
        <Image
          key={`active-trigger-${i}`}
          src="/union-arena/keywords/active_trigger.png"
          alt="Active Trigger"
          width={59}
          height={19}
          className="inline-block relative top-[-2px]"
        />
      ));
      text = reactStringReplace(text, "[Draw]", (match, i) => (
        <Image
          key={`draw-trigger-${i}`}
          src="/union-arena/keywords/draw_trigger.png"
          alt="Draw Trigger"
          width={59}
          height={19}
          className="inline-block relative top-[-2px]"
        />
      ));
    } else {
      text = reactStringReplace(text, "[Raid]", (match, i) => (
        <Image
          key={`raid-${i}`}
          src="/union-arena/keywords/raid.png"
          alt="Raid"
          width={80}
          height={21}
          className="inline-block relative top-[-2px]"
        />
      ));
    }
    return text;
  }

  // Helper function for italic text replacement
  function applyItalicReplacement(text: React.ReactNode[], idx: number) {
    const italicPhrases = [
      "Switch to active. May move to the front line.",
      "(When this character attacks and deals direct damage, deal 2 damage instead)",
      "(When this character attacks and deals direct damage, deal 2 damage instead.)",
      "(When this character attacks and wins a battle, deal 1 damage to your opponent.)",
      "(You may target a character on your opponent's front line and attack it with this character. If you do, your opponent cannot block)",
      "(The character battling this character loses",
      "for the duration of this battle.)",
      "(During your movement phase, you may move this card from your front line to your energy line.)",
    ];

    let replaced = text;
    for (const phrase of italicPhrases) {
      replaced = reactStringReplace(replaced, phrase, (match) => (
        <em
          key={`italic-${idx}-${phrase.slice(0, 10)}`}
          className="text-gray-400"
        >
          {match}
        </em>
      ));
    }
    return replaced;
  }

  // Apply trigger replacements
  replacedText = applyTriggerReplacements(replacedText);

  // Keyword replacements (same as before)
  const keywordImages: [string, string, number, number][] = [
    ["[Damage (2)]", "damage2", 70, 16],
    ["[When Played]", "when_played", 89, 16],
    ["[When Attacking]", "when_attacking", 104, 16],
    ["[Activate: Main]", "activate_main", 114, 16],
    ["[If on the Front Line]", "if_on_the_front_line", 147, 16],
    ["[Once Per Turn]", "once_per_turn", 91, 16],
    ["[During Your Turn]", "during_your_turn", 112, 16],
    ["[During Opponent's Turn]", "during_opponents_turn", 158, 16],
    ["[Nullify Impact]", "nullify_impact", 95, 16],
    ["[Switch to Resting]", "switch_to_resting", 118, 16],
    ["[Place 1 Card From Hand Into Sideline]", "outside", 237, 16],
    ["[When Sidelined]", "when_sidelined", 114, 16],
    ["[Impact (1)]", "impact1", 70, 16],
    ["[Impact]", "impact", 70, 16],
    ["[Snipe]", "snipe", 56, 16],
    ["[Step]", "step", 54, 16],
    ["[Pay 1 AP]", "pay_1ap", 62, 16],
    ["[Green×3]", "hexa_green3", 22, 25],
    ["[Green x 1]", "hexa_green1", 22, 25],
  ];

  for (const [text, img, w, h] of keywordImages) {
    replacedText = reactStringReplace(replacedText, text, (match, i) => (
      <Image
        key={`${img}-${i}`}
        src={`/union-arena/keywords/${img}.png`}
        alt={text.replace(/[\[\]]/g, "")}
        width={w}
        height={h}
        className="inline-block relative top-[-2px]"
      />
    ));
  }

  // Parse description into lines split by <br>, then identify bullet lines
  const parts = escaped.split(/<br\s*\/?>/gi);

  // Separate normal lines and bullet lines
  let result: React.ReactNode[] = [];
  let bulletLines: string[] = [];

  function flushBullets() {
    if (bulletLines.length > 0) {
      result.push(
        <ul key={`bullet-list`} className="list-disc list-inside mb-0 mt-0">
          {bulletLines.map((line, idx) => {
            // Apply keyword replacements to bullet text
            let replaced = reactStringReplace(line.trim());
            for (const [text, img, w, h] of keywordImages) {
              replaced = reactStringReplace(replaced, text, (match, i) => (
                <Image
                  key={`${img}-${idx}-${i}`}
                  src={`/union-arena/keywords/${img}.png`}
                  alt={text.replace(/[\[\]]/g, "")}
                  width={w}
                  height={h}
                  className="inline-block relative top-[-2px]"
                />
              ));
            }
            // Apply trigger replacements to bullet text
            replaced = applyTriggerReplacements(replaced);
            // Apply italic replacement
            replaced = applyItalicReplacement(replaced, idx);
            return (
              <li key={`bullet-${idx}`} className="mb-0">
                {replaced}
              </li>
            );
          })}
        </ul>,
      );
      bulletLines = [];
    }
  }

  for (const line of parts) {
    if (line.trim().startsWith("・")) {
      bulletLines.push(line.replace(/^・\s?/, ""));
    } else {
      flushBullets();
      // For normal line, run the replacements on it
      let replaced = reactStringReplace(line);

      // Keyword image replacements on normal lines
      for (const [text, img, w, h] of keywordImages) {
        replaced = reactStringReplace(replaced, text, (match, i) => (
          <Image
            key={`${img}-${i}`}
            src={`/union-arena/keywords/${img}.png`}
            alt={text.replace(/[\[\]]/g, "")}
            width={w}
            height={h}
            className="inline-block relative top-[-2px]"
          />
        ));
      }

      // Apply trigger replacements
      replaced = applyTriggerReplacements(replaced);
      // Apply italic replacement
      replaced = applyItalicReplacement(replaced, result.length);

      // Replace <em> and <br> tags properly
      const parsed = replaced.map((val) => {
        if (typeof val === "string") {
          return parse(val, {
            replace: (domNode: any) => {
              if (
                domNode.type === "tag" &&
                (domNode.name === "br" || domNode.name === "em")
              ) {
                return undefined;
              }
              if (domNode.type === "tag") {
                return `<${domNode.name}>${domNode.children?.[0]?.data || ""}</${domNode.name}>`;
              }
              return undefined;
            },
          });
        }
        return val;
      });

      result.push(...parsed);
      result.push(<br key={`br-after-line-${result.length}`} />);
    }
  }
  flushBullets();

  return result;
}
